import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, linkWithPhoneNumber, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { normalizePhoneInput, phoneLinkConflictMessage } from '../utils/authPhone';
import {
  mergeExistingPhoneAccount,
  isPhoneAlreadyRegisteredError,
  getPendingGoogleCredential,
  linkPendingGoogleToUser,
} from '../utils/authLinking';

const RECAPTCHA_CONTAINER_ID = 'rag-link-phone-recaptcha';

const RESEND_COOLDOWN_SEC = 45;

export default function LinkPhoneModal({ onLinked }) {
  const { refreshUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);
  const verificationIdRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  const hasPendingGoogle = !!getPendingGoogleCredential()
    || auth.currentUser?.providerData?.some((p) => p.providerId === 'google.com');

  useEffect(() => {
    if (resendSecondsLeft <= 0) return undefined;
    const t = setInterval(() => {
      setResendSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendSecondsLeft]);

  const destroyRecaptcha = () => {
    if (verifierRef.current) {
      try { verifierRef.current.clear(); } catch (_) {}
      verifierRef.current = null;
    }
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML = '';
    }
  };

  useEffect(() => {
    return () => destroyRecaptcha();
  }, []);

  /** Sends or re-sends SMS OTP via Firebase (each call issues a new SMS). */
  const sendSmsOTP = async (normalized) => {
    destroyRecaptcha();

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: 'invisible',
    });
    verifierRef.current = verifier;

    const confirmation = hasPendingGoogle
      ? await signInWithPhoneNumber(auth, normalized, verifier)
      : await linkWithPhoneNumber(auth.currentUser, normalized, verifier);
    confirmationRef.current = confirmation;
    verificationIdRef.current = confirmation.verificationId || null;
    destroyRecaptcha();
    setStep('otp');
    setOtp('');
    setResendSecondsLeft(RESEND_COOLDOWN_SEC);
  };

  const finalizeLinkedSession = async (linkedPhone) => {
    await auth.currentUser?.reload();

    const resolvedPhone =
      auth.currentUser?.phoneNumber ||
      linkedPhone ||
      normalizePhoneInput(phone.trim());

    if (resolvedPhone && auth.currentUser?.uid) {
      try {
        await api.updateVoiceConfig(auth.currentUser.uid, { owner_no: resolvedPhone });
      } catch (_) {
        // Profile may not exist yet — BusinessSetup sets owner_no on create
      }
    }

    await refreshUser();
    if (onLinked) onLinked();
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    const normalized = normalizePhoneInput(phone.trim());
    if (normalized.length < 10) {
      setError('Enter a valid phone number.');
      return;
    }

    setLoading(true);

    try {
      await sendSmsOTP(normalized);
    } catch (err) {
      destroyRecaptcha();
      if (isPhoneAlreadyRegisteredError(err)) {
        setError(
          'This number is already your SuperOwl mobile app account. Verify the OTP below — we will connect your accounts automatically.',
        );
        setStep('otp');
      } else if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many SMS requests. Wait a minute and try Resend OTP again.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendSecondsLeft > 0 || loading) return;
    setError('');
    const normalized = normalizePhoneInput(phone.trim());
    if (normalized.length < 10) {
      setError('Enter a valid phone number.');
      setStep('phone');
      return;
    }

    setLoading(true);
    try {
      await sendSmsOTP(normalized);
    } catch (err) {
      destroyRecaptcha();
      if (err.code === 'auth/too-many-requests') {
        setError('Too many SMS requests. Wait before requesting another code.');
      } else if (!isPhoneAlreadyRegisteredError(err)) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const tryMergeExistingPhoneAccount = async (otpCode) => {
    const verificationId =
      verificationIdRef.current || confirmationRef.current?.verificationId;
    if (!verificationId) {
      throw new Error('missing-verification-id');
    }
    await mergeExistingPhoneAccount(verificationId, otpCode);
  };

  const handleUsePhoneSignInInstead = async () => {
    setLoading(true);
    try {
      await signOut();
      navigate('/login?signin=phone');
    } catch (_) {
      window.location.href = '/login?signin=phone';
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const normalized = normalizePhoneInput(phone.trim());

    try {
      await confirmationRef.current.confirm(otp.trim());
      if (hasPendingGoogle) {
        await linkPendingGoogleToUser(auth.currentUser);
      }
      await finalizeLinkedSession(normalized);
    } catch (err) {
      if (err.code === 'auth/invalid-verification-code') {
        setError('Incorrect OTP. Please try again.');
        setLoading(false);
        return;
      }

      if (isPhoneAlreadyRegisteredError(err)) {
        try {
          await tryMergeExistingPhoneAccount(otp.trim());
          await finalizeLinkedSession(normalized);
          return;
        } catch (mergeErr) {
          if (mergeErr.message === 'missing-verification-id') {
            setError(phoneLinkConflictMessage());
          } else if (mergeErr.code === 'auth/invalid-verification-code') {
            setError('Incorrect OTP. Please try again.');
          } else {
            setError(
              mergeErr.message ||
                'Could not connect your accounts. Sign out and sign in with Phone using the same number.',
            );
          }
          setLoading(false);
          return;
        }
      }

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #1d2840 0%, #2d4a7a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '40px 32px',
        width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1d2840', marginBottom: '8px' }}>
          {hasPendingGoogle ? 'Verify your phone number' : 'Link Your Phone Number'}
        </h2>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '28px', lineHeight: '1.5' }}>
          {hasPendingGoogle
            ? 'Enter the same phone number you use in the SuperOwl mobile app. We will sign you into that account and connect Google.'
            : 'Your phone number will be your primary sign-in method. This is required to continue.'}
        </p>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
            padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 9876543210"
              required
              style={{
                width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '15px', outline: 'none',
                boxSizing: 'border-box', marginBottom: '6px',
              }}
            />
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '20px' }}>
              Include country code if not Indian (e.g. +1 for US)
            </p>
            <div ref={recaptchaContainerRef} id={RECAPTCHA_CONTAINER_ID} />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px', borderRadius: '30px', border: 'none',
                background: 'linear-gradient(to right, #1d2840, #3DA3DC)',
                color: '#fff', fontWeight: '600', fontSize: '15px',
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Sending…' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <p style={{ fontSize: '13px', color: '#374151', marginBottom: '16px' }}>
              OTP sent to <strong>{phone}</strong>
            </p>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Enter OTP
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code"
              maxLength={6}
              required
              style={{
                width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '20px', textAlign: 'center',
                letterSpacing: '8px', outline: 'none', boxSizing: 'border-box', marginBottom: '20px',
              }}
            />
            <div ref={recaptchaContainerRef} id={`${RECAPTCHA_CONTAINER_ID}-otp`} />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px', borderRadius: '30px', border: 'none',
                background: 'linear-gradient(to right, #1d2840, #3DA3DC)',
                color: '#fff', fontWeight: '600', fontSize: '15px',
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Verifying…' : hasPendingGoogle ? 'Verify & connect' : 'Verify OTP'}
            </button>
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={loading || resendSecondsLeft > 0}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                borderRadius: '30px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#1d2840',
                fontWeight: '600',
                fontSize: '14px',
                cursor: loading || resendSecondsLeft > 0 ? 'not-allowed' : 'pointer',
                opacity: loading || resendSecondsLeft > 0 ? 0.6 : 1,
              }}
            >
              {loading
                ? 'Sending…'
                : resendSecondsLeft > 0
                  ? `Resend OTP in ${resendSecondsLeft}s`
                  : 'Resend OTP'}
            </button>
            <button
              type="button"
              onClick={() => void handleUsePhoneSignInInstead()}
              disabled={loading}
              style={{
                width: '100%',
                marginTop: '10px',
                padding: '10px',
                borderRadius: '30px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#1d2840',
                fontWeight: '600',
                fontSize: '13px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              Sign in with phone instead
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setOtp('');
                setError('');
                setResendSecondsLeft(0);
              }}
              style={{
                width: '100%',
                marginTop: '10px',
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Use a different number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
